import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useDragControls } from 'motion/react';
import { X } from 'lucide-react';
import { getColorHex } from '../utils/colors';
import { resolveProductImage } from '../utils/productUtils';
import { formatPrice } from '../data/products';

interface VariantBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  product: any;
  variants: any[];
  colors: string[];
  selectedColor: string;
  setSelectedColor: (c: string) => void;
  options: string[];
  selectedOption: string;
  setSelectedOption: (o: string) => void;
  activeVariant: any;
  onAdd: () => void;
  shortName: string;
  addedFeedback?: boolean;
}

/**
 * Resolves the best representative image for a given color from product data.
 * Priority: variant_groups gallery → variant image → empty string (hex fallback)
 */
function getColorImage(product: any, variants: any[], color: string): string {
  const normalizedColor = String(color || '').trim().toLowerCase();

  // 1. variant_groups gallery image
  const group = (product?.variant_groups || []).find(
    (g: any) => String(g?.color || '').trim().toLowerCase() === normalizedColor
  );
  const groupImage = (group?.images || [])
    .map((img: any) => (typeof img === 'string' ? img : img?.image_url))
    .find(Boolean);
  if (groupImage) return resolveProductImage(groupImage);

  // 2. First variant image for that color
  const variantImage = variants
    .filter((v: any) => String(v.color || '').trim().toLowerCase() === normalizedColor)
    .map((v: any) => v.image_url || v.image)
    .find(Boolean);
  if (variantImage) return resolveProductImage(variantImage);

  return '';
}

const VariantBottomSheet: React.FC<VariantBottomSheetProps> = ({
  isOpen,
  onClose,
  product,
  variants,
  colors,
  selectedColor,
  setSelectedColor,
  options,
  selectedOption,
  setSelectedOption,
  activeVariant,
  onAdd,
  shortName,
  addedFeedback = false,
}) => {
  const selectedCardRef = useRef<HTMLButtonElement | null>(null);
  const dragControls = useDragControls();
  const isSoldOut = !activeVariant || (activeVariant?.stock ?? 0) === 0;

  const activeImage =
    getColorImage(product, variants, selectedColor) ||
    resolveProductImage(activeVariant?.image_url || activeVariant?.image || product?.thumbnail_url);

  const promoPrice = activeVariant?.promo_price || activeVariant?.promoPrice;
  const displayPrice = promoPrice ?? activeVariant?.price ?? 0;
  const originalPrice = activeVariant?.price ?? 0;
  const hasPromo = !!promoPrice && promoPrice < originalPrice;

  // Scroll selected card into view when color changes
  useEffect(() => {
    if (!isOpen || !selectedCardRef.current) return;
    selectedCardRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [selectedColor, isOpen]);

  // Lock body scroll while sheet is open without snapping to top
  useEffect(() => {
    let scrollY = 0;
    if (isOpen) {
      scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
      document.body.style.touchAction = 'none';
    } else {
      const scrollYStr = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollYStr) {
        const parsedScrollY = parseInt(scrollYStr || '0', 10) * -1;
        window.scrollTo(0, parsedScrollY);
      }
    }
    return () => {
      const scrollYStr = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      if (scrollYStr) {
        const parsedScrollY = parseInt(scrollYStr || '0', 10) * -1;
        window.scrollTo(0, parsedScrollY);
      }
    };
  }, [isOpen]);

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    // Auto-select first option for new color
    const nextVariants = variants.filter((v: any) => v.color === color);
    const firstOpt = nextVariants
      .map((v: any) => (v.option_name || v.option || '').trim())
      .find(Boolean);
    if (firstOpt) setSelectedOption(firstOpt);
  };

  const sheet = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[80] bg-ink/40"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Bottom sheet panel */}
          <motion.div
            key="sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragSnapToOrigin={false}
            dragElastic={{ top: 0.05, bottom: 0.85 }}
            dragListener={false}
            dragControls={dragControls}
            onDragEnd={(_, info) => {
              if (info.velocity.y > 350 || info.offset.y > 120) {
                onClose();
              }
            }}
            className="fixed bottom-0 left-0 right-0 md:left-1/2 md:-translate-x-1/2 md:bottom-4 md:w-[480px] md:rounded-2xl md:border md:border-ink/8 md:shadow-2xl z-[90] bg-white flex flex-col max-h-[88vh]"
            role="dialog"
            aria-modal="true"
            aria-label="Pilih varian produk"
          >
            {/* Drag handle */}
            <div
              className="flex justify-center pt-3 pb-3 shrink-0 cursor-grab active:cursor-grabbing touch-y"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-10 h-[3px] bg-ink/15 rounded-full" />
            </div>

            {/* Sticky header strip */}
            <div className="flex items-center gap-3 px-4 pb-4 border-b border-ink/8 shrink-0">
              {/* Active variant thumbnail */}
              <div className="w-12 h-16 shrink-0 overflow-hidden bg-paper border border-ink/8">
                {activeImage ? (
                  <img
                    src={activeImage}
                    alt={`${shortName} - ${selectedColor}`}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div
                    className="w-full h-full"
                    style={{ backgroundColor: getColorHex(selectedColor) }}
                  />
                )}
              </div>

              {/* Name / selection / price */}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-muted/50 mb-0.5">
                  Pilih Varian
                </p>
                <p className="text-sm font-semibold text-ink leading-snug truncate">
                  {shortName}
                </p>
                {selectedColor && (
                  <p className="text-[11px] text-muted/70 mt-0.5 truncate">
                    {selectedColor}
                    {selectedOption ? ` · ${selectedOption}` : ''}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm font-bold text-brand-wine">
                    {formatPrice(displayPrice)}
                  </span>
                  {hasPromo && (
                    <span className="text-[11px] text-muted/50 line-through">
                      {formatPrice(originalPrice)}
                    </span>
                  )}
                </div>
              </div>

              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-8 h-8 flex items-center justify-center text-muted/50 hover:text-ink transition-colors"
                aria-label="Tutup"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 overscroll-contain">

              {/* Color section */}
              {colors.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted/50 mb-3">
                    Warna{' '}
                    <span className="text-ink/40">({colors.length})</span>
                  </p>

                  {/* Dynamic row horizontal scroll track depending on colors count */}
                  <div className="overflow-x-auto pb-2 -mx-4 px-4 hide-scrollbar snap-x snap-mandatory">
                    <div
                      className={`grid gap-2 grid-flow-col pr-4 ${
                        colors.length <= 14 ? 'grid-rows-1' : 'grid-rows-2'
                      }`}
                      style={{
                        gridAutoColumns: '68px'
                      }}
                    >
                      {(() => {
                        const isDoubleRow = colors.length > 14;
                        if (!isDoubleRow) {
                          return colors.map((color: string) => {
                            const isActive = selectedColor === color;
                            const colorImage = getColorImage(product, variants, color);
                            const hex = getColorHex(color);
                            return (
                              <motion.button
                                key={color}
                                type="button"
                                ref={isActive ? selectedCardRef : undefined}
                                onClick={() => handleColorSelect(color)}
                                whileTap={{ scale: 0.96 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                className={`flex flex-col items-center gap-1 focus:outline-none group snap-center w-[68px] ${isActive ? 'opacity-100' : 'opacity-90 hover:opacity-100'}`}
                                aria-pressed={isActive}
                                aria-label={`Pilih warna ${color}`}
                              >
                                <div className={`w-full aspect-[3/4] overflow-hidden border-2 transition-all duration-200 ${isActive ? 'border-brand-wine ring-1 ring-brand-wine ring-offset-1' : 'border-transparent hover:border-ink/20'}`}>
                                  {colorImage ? <img src={colorImage} alt={color} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" /> : <div className="w-full h-full" style={{ backgroundColor: hex }} />}
                                </div>
                                <span className={`text-[9px] uppercase tracking-[0.06em] text-center leading-tight w-full truncate transition-colors ${isActive ? 'text-brand-wine font-bold' : 'text-muted/60 font-medium group-hover:text-ink'}`}>
                                  {color}
                                </span>
                              </motion.button>
                            );
                          });
                        }

                        // Reorder for 2-row layout so reading order is horizontal (left-to-right, row-first)
                        const cols = Math.ceil(colors.length / 2);

                        // Map over reordered items but place them back in their correct layout tracks
                        // Grid flows col-first, so we map index to keep column structure correct.
                        const gridOrdered: string[] = Array(cols * 2).fill('');
                        for (let r = 0; r < 2; r++) {
                          for (let c = 0; c < cols; c++) {
                            const readIndex = r * cols + c;
                            if (readIndex < colors.length) {
                              // place at column c, row r
                              // column c starts at c * 2
                              gridOrdered[c * 2 + r] = colors[readIndex];
                            }
                          }
                        }

                        return gridOrdered.filter(Boolean).map((color: string) => {
                          const isActive = selectedColor === color;
                          const colorImage = getColorImage(product, variants, color);
                          const hex = getColorHex(color);
                          return (
                            <motion.button
                              key={color}
                              type="button"
                              ref={isActive ? selectedCardRef : undefined}
                              onClick={() => handleColorSelect(color)}
                              whileTap={{ scale: 0.96 }}
                              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                              className={`flex flex-col items-center gap-1 focus:outline-none group snap-center w-[68px] ${isActive ? 'opacity-100' : 'opacity-90 hover:opacity-100'}`}
                              aria-pressed={isActive}
                              aria-label={`Pilih warna ${color}`}
                            >
                              <div className={`w-full aspect-[3/4] overflow-hidden border-2 transition-all duration-200 ${isActive ? 'border-brand-wine ring-1 ring-brand-wine ring-offset-1' : 'border-transparent hover:border-ink/20'}`}>
                                {colorImage ? <img src={colorImage} alt={color} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" /> : <div className="w-full h-full" style={{ backgroundColor: hex }} />}
                              </div>
                              <span className={`text-[9px] uppercase tracking-[0.06em] text-center leading-tight w-full truncate transition-colors ${isActive ? 'text-brand-wine font-bold' : 'text-muted/60 font-medium group-hover:text-ink'}`}>
                                {color}
                              </span>
                            </motion.button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Option / Pilihan section (Bross etc.) */}
              {options.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted/50 mb-3">
                    Pilihan
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {options.map((opt: string) => {
                      const isActive = selectedOption === opt;
                      return (
                        <motion.button
                          key={opt}
                          type="button"
                          onClick={() => setSelectedOption(opt)}
                          whileTap={{ scale: 0.97 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                          className={`h-9 px-4 text-[11px] font-bold tracking-widest uppercase border transition-all duration-200 ${isActive
                            ? 'border-brand-wine bg-brand-wine text-white'
                            : 'border-ink/12 bg-white text-ink hover:border-brand-wine/40 hover:text-brand-wine'
                            }`}
                          aria-pressed={isActive}
                        >
                          {opt}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Bottom safe-area padding */}
              <div className="h-2" />
            </div>

            {/* Sticky footer CTA */}
            <div className="shrink-0 px-4 py-3 border-t border-ink/8 bg-white">
              {/* Stock indicator */}
              {activeVariant && (
                <div className="flex items-center gap-1.5 mb-2.5">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${isSoldOut ? 'bg-red-500' : 'bg-emerald-500'
                      }`}
                  />
                  <span
                    className={`text-[9px] font-bold tracking-[0.15em] uppercase ${isSoldOut ? 'text-red-500' : 'text-muted/40'
                      }`}
                  >
                    {isSoldOut ? 'Stok Habis' : 'Tersedia'}
                  </span>
                </div>
              )}

              <motion.button
                type="button"
                onClick={onAdd}
                disabled={isSoldOut}
                whileTap={!isSoldOut ? { scale: 0.98 } : {}}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className={`w-full h-12 text-[11px] font-black tracking-[0.2em] uppercase flex items-center justify-center transition-colors duration-200 ${isSoldOut
                  ? 'bg-ink/15 text-ink/30 cursor-not-allowed'
                  : addedFeedback
                    ? 'bg-emerald-600 text-white'
                    : 'bg-brand-wine text-white hover:bg-brand-wine/90 active:bg-brand-wine'
                  }`}
              >
                {isSoldOut
                  ? 'Habis Terjual'
                  : addedFeedback
                    ? 'Berhasil Ditambahkan'
                    : 'Masukkan ke Keranjang'}
              </motion.button>

              {/* Safe area for mobile home indicator */}
              <div className="h-safe-bottom" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(sheet, document.body);
};

export default VariantBottomSheet;
